import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Package } from "lucide-react";
import { AppShell } from "@/components/shell/AppShell";
import { ModuleHeader, FeedChips, Toolbar, PillSelect, Segmented, SectionTabs, EmptyState, Loading } from "@/components/kit";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { monthLabel, ACCENT } from "./osUtils.jsx";
import OsDashboard from "./OsExecView";
import OsMonthView from "./OsTeamsView";
import OsWeekView from "./OsWeekView";
import OsCommissionView from "./OsCommissionView";
import OsAgentDrawer from "./OsAgentDrawer";

const VIEWS = [
  { id: "dash", label: "Dashboard" },
  { id: "mom", label: "Month on Month" },
  { id: "wow", label: "Week on Week" },
  { id: "cp", label: "Commission & Penalty" },
];
const REGIONS = ["Overall", "India", "Qatar"];

export default function OutboundSalesPage() {
  const { tenantId, tenant } = useTenant() || {};
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(params.get("view") || "dash");
  const [region, setRegion] = useState(params.get("region") || "Overall");
  const [product, setProduct] = useState(params.get("product") || "");
  const [month, setMonth] = useState(params.get("month") || "");
  const [drawer, setDrawer] = useState(params.get("agent") || null);

  const load = useCallback((refresh = false) => {
    if (!tenantId) { setError("No client selected. Use the client switcher in the header."); return; }
    setBusy(true); setError(null);
    api.get(`/modules/outbound-sales/dashboard?tenant_id=${tenantId}&refresh=${refresh}`)
      .then((r) => {
        setData(r.data);
        setMonth((m) => (m === "all" || r.data.months.includes(m) ? m : r.data.coverage.sales?.slice(0, 7) || r.data.months.slice(-1)[0]));
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  }, [tenantId]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const next = {};
    if (view !== "dash") next.view = view;
    if (region !== "Overall") next.region = region;
    if (product) next.product = product;
    if (month) next.month = month;
    if (drawer) next.agent = drawer;
    setParams(next, { replace: true });
  }, [view, region, product, month, drawer, setParams]);

  const products = useMemo(() => (data ? data.products : []), [data]);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const ctx = { data, month: month === "all" ? "" : month, region, product: product || null, onOpenAgent: setDrawer };
  const ex = data?.excluded || {};
  const unmappedN = data ? Object.values(data.agents).filter((a) => a.region === "Unmapped").length : 0;

  return (
    <AppShell>
      <ModuleHeader testid="os-title" title="Outbound Sales" subtitle="Sales Performance" accent={ACCENT}
        context={`${tenant?.name || "No client"} · ${data?.months?.length ? `${monthLabel(data.months[0])} – ${monthLabel(data.months[data.months.length - 1])}` : "Workspace data"} · excluding cancelled & duplicate sales`}
        meta={data ? `Snapshot · ${(data.months_loaded || []).length} months loaded` : null} onRefresh={data ? () => load(true) : null} busy={busy} />

      {error ? (
        <EmptyState testid="os-error" title="No data for this module" body={error} isAdmin={isAdmin} action={{ to: "/settings?section=uploads", label: "Upload monthly data" }} />
      ) : !data || !month ? <Loading /> : (
        <>
          <FeedChips testid="os-coverage-banner" coverage={data.coverage} months={data.months_loaded || []} hint="Targets and Commission apply to the month they were uploaded for" />
          <Toolbar testid="os-toolbar">
            <Segmented testidPrefix="os-region" options={REGIONS} value={region} onChange={setRegion} />
            <PillSelect testid="os-month-select" icon={CalendarDays} value={month} onChange={(e) => setMonth(e.target.value)}>
              {data.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              {data.months.length > 1 && <option value="all">All months (till date)</option>}
            </PillSelect>
            <PillSelect testid="os-product-select" icon={Package} value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="">All Products</option>
              {products.map((p) => <option key={p} value={p}>{p}</option>)}
            </PillSelect>
            <span className="rounded-md bg-thamber/10 px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-[0.12em] text-thamber" data-testid="os-exclusion-note" title={`${(data.rows_total || 0).toLocaleString()} rows in Sales Dump = ${data.sales.reduce((a, x) => a + x.n, 0).toLocaleString()} valid + ${ex.churn} cancelled + ${ex.dup} duplicates${data.rows_skipped ? ` + ${data.rows_skipped} without date/agent` : ""}`}>Not counted: {ex.churn} cancelled (Churn Flag = Yes) & {ex.dup} duplicates (Duplicate ≥ 2)</span>
            {data.unmatched?.length > 0 && <p className="mk-sub px-1 text-thamber" data-testid="os-unmatched" title={data.unmatched.join(", ")}>⚠ {data.unmatched.length} target / commission name(s) not in Sales Dump</p>}
            {unmappedN > 0 && <p className="mk-sub px-1 text-thamber" data-testid="os-unmapped-note" title="IDs not in the Agent Mapping master · counted in Overall, excluded from India and Qatar · add them in Settings → Agent Mapping and re-map the month">⚠ {unmappedN} agent ID(s) Unmapped — not in the Agent Mapping master</p>}
          </Toolbar>
          <SectionTabs testidPrefix="os-view" tabs={VIEWS} value={view} onChange={setView} accent={ACCENT} />

          {view === "dash" && <OsDashboard {...ctx} />}
          {view === "mom" && <OsMonthView {...ctx} />}
          {view === "wow" && <OsWeekView {...ctx} />}
          {view === "cp" && <OsCommissionView {...ctx} />}
          {drawer && <OsAgentDrawer data={data} u={drawer} month={ctx.month} onClose={() => setDrawer(null)} />}
        </>
      )}
    </AppShell>
  );
}
